namespace Portico.Services {

	import Inject = Portico.Decorators.Inject;
	import Service = Portico.Decorators.Service;
	import pme = Portico.Models.Enterprise;
	import pmg = Portico.Models.Gateway;
	import ps = Portico.Services;
	import psga = apigateway.client;
	import psgm = apigateway.models;

	export interface IEnrollmentService {
		checkMemberNumber: (memberNumber: string, haveACard: boolean, partyId: number, checkForEnrollment: boolean) => angular.IPromise<psgm.EnrollmentIsValidMemberNumberModel>;
		create: (enrollment?: pme.Enrollment) => angular.IPromise<pme.Enrollment>;
		enrollPatient: (enrollRequestModel: pme.EnrollRequestModel
			, shouldVerifyEligibility: boolean
			, payorCoverageAreas: psgm.PayorCoverageEligibilityRequestModel[]
			, enrollmentInput?: psgm.ScriptsEnrollmentEnrollPatientEnrollmentInputModel
			, programId?: string | number) => angular.IPromise<psgm.EnrollmentV2ProcessingResultModel>
			, caseInitiator?: pmg.EnrollPatientCaseInitiator;
		getBestEnrollment: (patient: pme.Patient) => angular.IPromise<psgm.EnrollmentModel>;
		getBestEnrollmentV2: (patient: pme.Patient) => angular.IPromise<psgm.EnrollmentModel[]>;
	}

	@Service('app', 'enrollmentService')
	@Inject('$q', 'dataService', 'EdgeApi', 'EdgeEnrollmentApi')
	export class EnrollmentService implements IEnrollmentService {
		constructor(
			private $q: angular.IQService,
			private dataService: ps.IDataService,
			private edgeApi: psga.EdgeApi,
			private edgeEnrollmentApi: psga.EdgeEnrollmentApi
		) { }

		checkMemberNumber(memberNumber: string, haveACard: boolean, partyId: number, checkForEnrollment: boolean) {
			const request = {
				memberNumber,
				haveACard,
				partyId,
				checkForEnrollment
			} as psgm.EnrollmentIsValidMemberNumberRequestModel;

			return this.$q.when(this.edgeEnrollmentApi.enrollmentCheckMemberNumber(request).then((response) => {
				return response.data.data;
			}));
		}

		create(enrollment: pme.Enrollment = null): angular.IPromise<pme.Enrollment> {
			return this.$q.when(new pme.Enrollment(enrollment));
		}

		enrollPatient(enrollRequestModel: pme.EnrollRequestModel
			, shouldVerifyEligibility: boolean
			, payorCoverageAreas: psgm.PayorCoverageEligibilityRequestModel[]
			, enrollmentInput?: psgm.ScriptsEnrollmentEnrollPatientEnrollmentInputModel
			, programId?: string | number
			, caseInitiator?: pmg.EnrollPatientCaseInitiator) {

			const temp = new pme.EnrollRequestModel(enrollRequestModel);
			const sourceId = this.dataService.getEnvironmentId('source-id');
			const edgePatientEnrollmentRequestModel = temp.mapToEnterpriseCoordinatorAccountUpsearchRequestModel(sourceId);
			const overrideProgramId = programId ? programId.toString() : null;

			const request = {
				account: edgePatientEnrollmentRequestModel,
				shouldVerifyEligibility: shouldVerifyEligibility,
				payorCoverageAreas: payorCoverageAreas,
				enrollmentInput,
				caseInitiator,
			} as psgm.EnrollPatientRequestModel;

			return this.$q.when(this.edgeApi.patientEnrollmentEnrollPatient(request, overrideProgramId).then((response) => {
				response.data.data.benefitResult = response.data.data.benefitResult ?? {} as psgm.EnrollmentV2ProcessingBenefitModel;
				response.data.data.benefitResult.claimEligibilityDate = <moment.Moment><unknown>response.data.data.benefitResult.claimEligibilityDate?.toDate();
				response.data.data.benefitResult.endDate = <moment.Moment><unknown>response.data.data.benefitResult.endDate?.toDate();
				response.data.data.benefitResult.queuedProcessingDate = <moment.Moment><unknown>response.data.data.benefitResult.queuedProcessingDate?.toDate();
				response.data.data.benefitResult.startDate = <moment.Moment><unknown>response.data.data.benefitResult.startDate?.toDate();
				response.data.data.enrollment = response.data.data.enrollment ?? {} as psgm.EnrollmentV2EnrollmentModel;
				response.data.data.enrollment.claimEligibilityDate = <moment.Moment><unknown>response.data.data.enrollment.claimEligibilityDate?.toDate();
				response.data.data.enrollment.createDate = <moment.Moment><unknown>response.data.data.enrollment.createDate?.toDate();
				response.data.data.enrollment.endDate = <moment.Moment><unknown>response.data.data.enrollment.endDate?.toDate();
				response.data.data.enrollment.startDate = <moment.Moment><unknown>response.data.data.enrollment.startDate?.toDate();
				return response.data.data;
			}));
		}

		getBestEnrollment(patient: pme.Patient) {
			const getBestEnrollmentRequestModel: psgm.GetBestEnrollmentRequest = {
				firstName: patient.firstName,
				lastName: patient.lastName,
				dateOfBirth: moment(patient.dateOfBirth),
				zip: patient.addresses && patient.addresses.length > 0 ? patient.addresses[0].zip : null,
				gender: pmg.Gender[patient.gender]
			}

			return this.$q.when(this.edgeApi.enrollmentGetBestEnrollment(getBestEnrollmentRequestModel).then((response) => {
				response.data.data.claimEligibilityDate = <moment.Moment><unknown>response.data.data.claimEligibilityDate?.toDate();
				response.data.data.createdOn = <moment.Moment><unknown>response.data.data.createdOn?.toDate();
				response.data.data.endDate = <moment.Moment><unknown>response.data.data.endDate?.toDate();
				response.data.data.reenrollmentEligibilityDate = <moment.Moment><unknown>response.data.data.reenrollmentEligibilityDate?.toDate();
				response.data.data.startDate = <moment.Moment><unknown>response.data.data.startDate?.toDate();
				response.data.data.updatedOn = <moment.Moment><unknown>response.data.data.updatedOn?.toDate();
				return response.data.data;
			}));
		}

		getBestEnrollmentV2(patient: pme.Patient) {
			const getBestEnrollmentRequestModel: psgm.GetBestEnrollmentV2RequestModel = {
				firstName: patient.firstName,
				lastName: patient.lastName,
				dateOfBirth: moment(patient.dateOfBirth),
				zip: patient.addresses && patient.addresses.length > 0 ? patient.addresses[0].zip : null,
				gender: pmg.Gender[patient.gender]
			}

			return this.$q.when(this.edgeEnrollmentApi.enrollmentGetBestEnrollmentsV2(getBestEnrollmentRequestModel).then((response) => {
				response.data.data.forEach(e => {
					e.claimEligibilityDate = <moment.Moment><unknown>e.claimEligibilityDate?.toDate();
					e.createdOn = <moment.Moment><unknown>e.createdOn?.toDate();
					e.endDate = <moment.Moment><unknown>e.endDate?.toDate();
					e.reenrollmentEligibilityDate = <moment.Moment><unknown>e.reenrollmentEligibilityDate?.toDate();
					e.startDate = <moment.Moment><unknown>e.startDate?.toDate();
					e.updatedOn = <moment.Moment><unknown>e.updatedOn?.toDate();
				});
				return response.data.data;
			}));
		}
	}
}